/**
 * PairingView — headless pairing screen.
 *
 * Headless: no `browser.*`, no `chrome.*`, no IPC, no storage hooks.
 * The component is purely presentational — every state value (the
 * 6-digit code, the countdown, the error message) and every
 * transition (retry click) arrives via props. Hosts wire their own
 * pairing state machines: the extension's `START_PAIRING` IPC +
 * `pairingJustConfirmed` storage-watch, or the web's inline keypair-
 * gen + SSE/poll race in `services/pairing.ts`. Mirrors the
 * conventions established by the other headless feature wrappers in
 * this package (`SettingsPanel`, `HeaderBar`, `AppShell`).
 *
 * Five terminal states, driven externally:
 *
 *   - `loading`   — request is in flight; status line announces
 *                    "Requesting pairing code…".
 *   - `showCode`  — 6-box code grid filled with `code`, countdown
 *                    rendered from `secondsLeft` with three-tier
 *                    urgency tone (neutral / secondary @≤60s /
 *                    negative @≤30s).
 *   - `confirmed` — heading swaps to `pairedSuccessfully`, body
 *                    swaps to a centered Check icon + sr-only
 *                    success line.
 *   - `expired`   — empty code grid + "Code expired" status + retry
 *                    button.
 *   - `error`     — empty code grid + `errorMessage` status (or the
 *                    `failedToStartPairing` fallback if empty) +
 *                    retry button.
 *
 * Accessibility:
 *   - The 6-box grid carries `role="status"` (implicit polite live
 *     region). The visible boxes are `aria-hidden` because they're
 *     decorative; an sr-only sibling span carries the announcement
 *     text. Every state transition produces a new live-region text
 *     so AT users hear the change.
 *   - Two-phase publish of the live-region text (empty on mount,
 *     real text via effect) so screen readers see a content CHANGE
 *     rather than a pre-populated string. role="status" only
 *     announces changes.
 *   - In `showCode`, digits are joined with spaces in the live
 *     region (e.g. "4 7 2 8 1 3") so AT announces them as a
 *     positional sequence rather than a 6-figure number.
 *   - The countdown timer is NOT a live region — `aria-live="polite"`
 *     there would batter AT users with a per-second
 *     "2:59… 2:58… 2:57…" stream. The visible tone shift is the
 *     sighted-user cue; the parent grid's role="status" handles
 *     announce-on-change.
 *
 * `supportEmail` is independent from `labels.needAppText` — pass both
 * to render the footer help hint, omit either to suppress the entire
 * footer line.
 */

import { Check } from "@untitledui/icons";
import { type ReactNode, useEffect, useState } from "react";
import { splitOnEmailSentinel } from "../../utils/emailSentinel";
import { Button } from "../Button";
import { Logo } from "../Logo";
import styles from "./PairingView.module.css";

const CODE_LENGTH = 6;

/**
 * Five terminal states the host's pairing FSM produces. Loose union
 * (rather than discriminated record) so the component takes the
 * minimum prop surface — `code` / `secondsLeft` / `errorMessage` are
 * each optional and only consumed in the state where they apply.
 */
export type PairingViewState = "loading" | "showCode" | "confirmed" | "expired" | "error";

/**
 * i18n bag for every visible/announced string.
 *
 * `needAppText` carries an `__EMAIL__` sentinel that the component
 * splits on to inline a `<a href="mailto:...">` anchor at the
 * placeholder location. When `supportEmail` is omitted (or
 * `needAppText` is empty), the footer help hint is suppressed
 * entirely.
 */
export interface PairingViewLabels {
  /** Heading shown in the loading / showCode states (and the
   *  expired / error states UNLESS `pairingFailed` is provided). */
  enterCodeInstruction: string;
  /** Heading + sr-only success line in the confirmed state. */
  pairedSuccessfully: string;
  /**
   * Optional heading override for the `expired` and `error` states.
   * When omitted, the heading falls back to `enterCodeInstruction`
   * — which reads as a contradictory directive next to a red error
   * panel (an instruction to "enter this code on your phone" while
   * the body says the request failed). Hosts that want to surface
   * the contextual "Pairing failed" / "Code expired" framing should
   * pass this; hosts that want the legacy single-heading behavior
   * (extension's pre-Phase-E shape) leave it undefined.
   */
  pairingFailed?: string;
  /** Status line + sr-only announce when state === "loading". */
  requestingPairingCode: string;
  /** Status line + sr-only announce when state === "expired". */
  codeExpired: string;
  /** Try Again button label (rendered in the expired / error states). */
  tryAgain: string;
  /** Fallback announce text when state === "error" and `errorMessage` is empty. */
  failedToStartPairing: string;
  /** Footer help-hint copy with an `__EMAIL__` sentinel. Optional. */
  needAppText?: string;
}

export interface PairingViewProps {
  /** Pairing FSM state (host-owned). */
  state: PairingViewState;
  /** 6-digit code — required when `state === "showCode"`, ignored otherwise. */
  code?: string;
  /** Seconds remaining on the pairing window — required when
   *  `state === "showCode"`, ignored otherwise. Drives the M:SS
   *  display and the three-tier urgency tone. */
  secondsLeft?: number;
  /** Server / network error message — used as the status line + the
   *  sr-only announce when `state === "error"`. Falls back to
   *  `labels.failedToStartPairing` when empty. */
  errorMessage?: string;
  /** Click handler for the Try Again button. Rendered only when
   *  `state === "expired"` OR `state === "error"`. When omitted in
   *  those states the retry row is suppressed entirely — a no-op
   *  interactive button is worse than no button. */
  onRetry?: () => void;
  /** Email address inlined into `labels.needAppText` at the
   *  `__EMAIL__` sentinel. When omitted, the footer help hint is
   *  suppressed regardless of `needAppText`. */
  supportEmail?: string;
  /** i18n bag for every visible string. */
  labels: PairingViewLabels;
}

/** Format seconds as M:SS for the countdown timer. */
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function PairingView({
  state,
  code = "",
  secondsLeft = 0,
  errorMessage = "",
  onRetry,
  supportEmail,
  labels,
}: PairingViewProps) {
  // Boxes are always 6 cells; fill from `code` when present, leave
  // empty otherwise. Layout stays stable across state transitions
  // (no width/height shift between loading → showCode → expired,
  // which would be jarring).
  const showFilledCode = state === "showCode";

  // Text for the codeGrid's live region. This is read by screen
  // readers both when the user navigates to the region AND when the
  // string changes (a live-region transition). The visible boxes are
  // `aria-hidden` for purely decorative layout, so this string is the
  // ONLY accessible text content inside the `role="status"` container
  // — without it, AT users hear nothing on state changes and never
  // learn the pairing code.
  //
  // In `showCode`, digits are joined with spaces so screen readers
  // announce "4 7 2 8 1 3" rather than "four hundred seventy-two
  // thousand…" — the code is a positional sequence, not a number.
  //
  // The `confirmed` state takes a separate render branch below and
  // never reaches this code path; intentionally not handled here.
  let computedLiveRegionText = "";
  if (showFilledCode) {
    computedLiveRegionText = `${labels.enterCodeInstruction}: ${code.split("").join(" ")}`;
  } else if (state === "loading") {
    computedLiveRegionText = labels.requestingPairingCode;
  } else if (state === "expired") {
    computedLiveRegionText = labels.codeExpired;
  } else if (state === "error") {
    // Belt-and-suspenders: prefer the host-supplied errorMessage but
    // fall back to the generic-failure string (not the "Pair your
    // phone" heading) so AT users still hear an error signal if the
    // host ever leaves errorMessage empty.
    computedLiveRegionText = errorMessage || labels.failedToStartPairing;
  }

  // Two-phase publish of the announcement text: render once with ""
  // on mount, then update to the real text via effect so screen
  // readers see a content CHANGE (empty → text) rather than a pre-
  // populated string. role="status" live regions only announce
  // changes — content present on mount is silently skipped. This
  // matters for the initial "Requesting pairing code…" announcement;
  // subsequent transitions (loading → showCode etc.) are naturally
  // changes and would announce either way.
  const [liveRegionText, setLiveRegionText] = useState("");
  useEffect(() => {
    setLiveRegionText(computedLiveRegionText);
  }, [computedLiveRegionText]);

  // Footer help hint — only rendered when both `needAppText` and
  // `supportEmail` are present. When either is missing the entire
  // line is suppressed (avoids "Need the Reflecto app? Email  for
  // access." with an empty anchor). Plain conditional rather than
  // an IIFE so the rendered shape is greppable + the `null` early
  // exit reads top-down instead of through a function-call return.
  let helpHint: ReactNode = null;
  if (labels.needAppText && supportEmail) {
    const { before, after } = splitOnEmailSentinel(labels.needAppText);
    helpHint = (
      <p className={styles.helpHint}>
        {before}
        <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
        {after}
      </p>
    );
  }

  // Heading derivation — same top-down conditional pattern as
  // `helpHint` above so the three branches read sequentially:
  //   - confirmed  → success heading
  //   - error|expired with `pairingFailed` set → contextual override
  //   - everything else (including error/expired with no override) →
  //     the legacy enterCodeInstruction heading
  let headingText = labels.enterCodeInstruction;
  if (state === "confirmed") {
    headingText = labels.pairedSuccessfully;
  } else if ((state === "error" || state === "expired") && labels.pairingFailed) {
    headingText = labels.pairingFailed;
  }

  return (
    <div className={styles.container}>
      {/* First-run brand moment. AppShell omits HeaderBar chrome until
       *  the user is paired, so the Logo anchors this screen. `lg`
       *  (32px) matches Figma 1148:562 and gives the wordmark enough
       *  presence to own the top third of the screen without tipping
       *  into `xl` (48px) dominance. `decorative` because the h2
       *  below already announces the directive. */}
      <div className={styles.brandMark}>
        <Logo size="lg" decorative />
      </div>

      {/* Single-line heading. The 6-box grid below makes "this is a
       *  pairing code" implicit; the directive alone carries both the
       *  identity and the action.
       *
       *  In the `confirmed` state the body swaps to a success
       *  checkmark (no code shown), so the heading text swaps too —
       *  otherwise a screen-reader user navigating by headings would
       *  hear "Enter this code…" with no code beneath it. The h2
       *  element stays in the tree (no reflow); only its text
       *  updates. The error / expired states optionally swap to
       *  `labels.pairingFailed` so the heading doesn't read as a
       *  contradictory "enter this code" instruction next to a red
       *  error panel; falls back to `enterCodeInstruction` when the
       *  host omits the override (extension's pre-Phase-E shape).
       *  Heading derivation lives above next to `helpHint`. */}
      <h2 className={styles.title}>{headingText}</h2>

      {/* State-specific body */}
      {state === "confirmed" ? (
        <div className={styles.centered}>
          <div className={styles.checkmark}>
            <Check size={48} aria-hidden="true" />
          </div>
          {/* aria-hidden because the h2 above already announces
           *  "Paired successfully!" in the confirmed state. Without
           *  this, screen readers would announce the same text twice
           *  (once from the heading, once from this paragraph). The
           *  `<p>` exists purely for the green success tone. */}
          <p className={styles.success} aria-hidden="true">
            {labels.pairedSuccessfully}
          </p>
        </div>
      ) : (
        <div className={styles.codeArea}>
          {/* Live region for code-state announcements. role="status"
           *  gives an implicit polite live region; the visually-hidden
           *  <span> below carries the text that screen readers
           *  announce on transition. The individual code boxes are
           *  decorative (aria-hidden) — without the hidden span the
           *  region's accessible text would be "" and AT users would
           *  miss every state change AND never hear the code digits. */}
          <div className={styles.codeGrid} role="status">
            <span className={styles.srOnly}>{liveRegionText}</span>
            {Array.from({ length: CODE_LENGTH }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: digits are positional; index IS the identity
                key={i}
                className={styles.codeBox}
                aria-hidden="true"
              >
                {showFilledCode && code[i] ? (
                  <span className={styles.codeDigit}>{code[i]}</span>
                ) : null}
              </div>
            ))}
          </div>

          {/* Status line beneath the grid — swaps per state. NOT a
           *  live region itself: the parent codeGrid already carries
           *  `role="status"` and its sr-only sibling provides the
           *  announce-on-change text. Nesting a second live region
           *  here would cause double-announcements in some screen
           *  readers. */}
          {state === "loading" && <p className={styles.status}>{labels.requestingPairingCode}</p>}
          {state === "showCode" && (
            <p
              // Tone escalates as the code's life runs out: tertiary
              // neutral → secondary warn (≤60s) → negative urgent (≤30s).
              // Encodes urgency without adding copy.
              //
              // NOT a live region: `aria-live="polite"` here would
              // fire an announcement every second for the full
              // countdown, battering screen-reader users with
              // "2:59… 2:58… 2:57…" interruptions. The visible tone
              // shift is a sighted-user cue.
              className={[
                styles.timer,
                secondsLeft <= 30
                  ? styles.timerUrgent
                  : secondsLeft <= 60
                    ? styles.timerWarning
                    : null,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {formatTime(secondsLeft)}
            </p>
          )}
          {state === "expired" && <p className={styles.errorText}>{labels.codeExpired}</p>}
          {state === "error" && (
            <p className={styles.errorText}>{errorMessage || labels.failedToStartPairing}</p>
          )}

          {(state === "expired" || state === "error") && onRetry && (
            // Matches the Figma 1148:562 primary CTA (cyan→green
            // gradient, full-width, 40px tall) via the Button
            // primitive's `primary` + `fullWidth` props — no
            // hand-rolled retry styles. The retry row stretches to
            // the container's full width so the button can fill it.
            <div className={styles.retryRow}>
              <Button variant="primary" size="m" fullWidth onClick={onRetry}>
                {labels.tryAgain}
              </Button>
            </div>
          )}
        </div>
      )}

      {helpHint}
    </div>
  );
}
